import * as React from 'react';

interface HeaderTitleValue {
  title: string | null;
  subtitle: string | null;
}

interface HeaderTitleContextValue extends HeaderTitleValue {
  setHeaderTitle: (value: HeaderTitleValue) => void;
}

const HeaderTitleContext = React.createContext<HeaderTitleContextValue | null>(null);

export function HeaderTitleProvider({ children }: { children: React.ReactNode }) {
  const [value, setValue] = React.useState<HeaderTitleValue>({ title: null, subtitle: null });

  const setHeaderTitle = React.useCallback((next: HeaderTitleValue) => {
    setValue(next);
  }, []);

  const ctx = React.useMemo(
    () => ({ ...value, setHeaderTitle }),
    [value, setHeaderTitle]
  );

  return <HeaderTitleContext.Provider value={ctx}>{children}</HeaderTitleContext.Provider>;
}

export function useHeaderTitleContext() {
  const ctx = React.useContext(HeaderTitleContext);
  if (!ctx) {
    throw new Error('useHeaderTitleContext must be used within HeaderTitleProvider');
  }
  return ctx;
}

/** Pages call this to populate the shared header bar's left-side title/subtitle. */
export function useSetHeaderTitle(title: string | null, subtitle: string | null = null) {
  const { setHeaderTitle } = useHeaderTitleContext();
  React.useEffect(() => {
    setHeaderTitle({ title, subtitle });
    return () => setHeaderTitle({ title: null, subtitle: null });
  }, [title, subtitle, setHeaderTitle]);
}
